/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Button, Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import {
  getUserTimezone,
  serializeDateValue,
  sortArray,
  startOfWeek,
} from "utils/miscUtils";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { dailyCoinReportColumns } from "utils/tableHelper/reportsTableHelper";
import {
  exportReport,
  getPbfCoinSummary,
  ReportTypes,
} from "services/reportsService";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleDateInput } from "components/FormDateInput";
import useFilterOptionsHook from "hooks/useFilterOptions";
import ExportModal from "screens/modals/ExportModal";
import useOpenClose from "hooks/useOpenClose";
import UploadIcon from "@mui/icons-material/Upload";
import { FilterSectionProps } from "types/filters";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
    gap: 5,
    marginLeft: 10,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  icon: {
    fontSize: "20px",
  },
}));

const RowActions = ({ data }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => setAnchorEl(event.currentTarget);

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(`/player-card/edit/${data?._id}`);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
      </Menu>
    </>
  );
};

const FilterSection = ({
  handleChange = () => {},
  onSubmitClick = () => {},
  onReset = () => {},
  filterOptions,
}: FilterSectionProps) => {
  const classes = useStyles();
  const customStyle = { height: 30 };

  return (
    <Box className={classes.root}>
      <Box className={classes.field} style={{ marginLeft: "0px" }}>
        <SimpleDateInput
          label="From:"
          id="startTime"
          name="startTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.startTime}
          onChange={(date) => handleChange({ name: "startTime", value: date })}
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleDateInput
          label="To:"
          id="endTime"
          name="endTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.endTime}
          onChange={(date) => handleChange({ name: "endTime", value: date })}
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={{ height: 30 }}
          type="text"
          label="User ID"
          name="userId"
          value={filterOptions.userId}
        />
      </Box>
      <Box className={classes.buttonContainer}>
        <Button className={classes.button} onClick={onSubmitClick}>
          Submit
        </Button>
        <Button className={classes.button} onClick={onReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

const DailyCoinReport = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);

  const { isOpen, open, close } = useOpenClose();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });
  const {
    filterOptions,
    onChange: setFilterOptions,
    reset,
  } = useFilterOptionsHook({
    initialValues: {
      userId: null,
      startTime: startOfWeek(),
      endTime: new Date(),
    },
  });
  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const buildReportPayload = () => {
    const { startTime, endTime, userId } = filterOptions;
    let _start = new Date(startTime);
    let _end = new Date(endTime);
    if (_start > _end) {
      showSnackbar("Start date should not be greater than end date", "error");
      return false;
    }

    return {
      pageNo,
      limit: pageSize,
      sortKey: null,
      sortDir: null, //orderDirection === "asc" ? "ASC" : "DESC",
      startTime: serializeDateValue(startTime),
      endTime: serializeDateValue(endTime),
      timezone: getUserTimezone(),
      userId: userId,
      skipOverallSummary: true,
    };
  };

  const fetchPlayerReport = async () => {
    setLoading(true);
    try {
      const payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const response = await getPbfCoinSummary(payload);

      setFilteredList(response?.data?.reportSummary || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const onExportReportMail = async (email) => {
    setLoading(true);
    try {
      let payload: any = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const { platform, operator, brand } = payload;

      if (platform === "all") payload.platform = null;
      if (operator === "all") payload.operator = null;
      if (brand === "all") payload.brand = null;

      payload = { ...payload, type: ReportTypes.dailyCoin, email };

      await exportReport(payload);

      setLoading(false);
      showSnackbar("Data request, will be sent to the email.");
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const handleFilterChange = ({ name, value }: any) => {
    setFilterOptions({ name, value });
  };

  useEffect(() => {
    setColumns(dailyCoinReportColumns(filterOptions));
    fetchPlayerReport();
  }, [pageNo, pageSize]);

  return (
    <ContentShell
      title="Daily PBF Coin Report"
      loading={loading}
      actions={
        <Button
          startIcon={<UploadIcon className={styles.icon} />}
          variant="contained"
          onClick={open}
        >
          Export
        </Button>
      }
    >
      <Box className={styles.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchPlayerReport}
          onReset={reset}
        />
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
        />
      </Box>
      <ExportModal
        list={filteredList}
        fileName="DailyCoin_Report"
        filterOptions={filterOptions}
        open={isOpen}
        onClose={close}
        onSendEmail={onExportReportMail}
      />
    </ContentShell>
  );
};

export default DailyCoinReport;
